import React, { useEffect } from 'react';
import { Button } from '@mui/material';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import Strings from '../../../../i18n';
import { ErrorText } from '../../../../components/auth/Profile/completeProfileStyles';
import {
  StyledContainerFooterSideBar,
  StyledContainerItems,
  StyledTitleSideBar
} from '../../../../components/commons/SideBarComponents/sideBarComponentsStyles';
import { SideBarEditablePropertyItem } from '../../../../components/commons/SideBarComponents/EditablePropertyItem/sideBarEditablePropertyItem';
import { createPartNumberSideBarProps } from './createPartNumberSideBarProps';
import { partNumberAction } from '../../../../utils/validators';
import { useCreatePartNumberMutation } from '../../../../services/manufacture/manufactureService';

export const CreatePartNumberSideBar = ({
  reloadTable,
  setOpenSideBar,
  openRightSideBarForMethod
}: createPartNumberSideBarProps) => {
  const [createPartNumber, { isSuccess, isError, error }] =
    useCreatePartNumberMutation();

  useEffect(() => {
    if (isSuccess) {
      toast.success('Data was saved successfully');
      reloadTable();
      setOpenSideBar(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError && error) {
      if (
        'data' in error &&
        typeof error.data === 'object' &&
        error.data !== null &&
        'message' in error.data
      ) {
        toast.error((error.data as { message: string }).message);
      } else {
        toast.error('An error occurred');
      }
    }
  }, [isError, error]);

  return (
    <Formik
      enableReinitialize
      validationSchema={partNumberAction}
      initialValues={{
        partNumber: '',
        series: '',
        product: '',
        model: '',
        sku: '',
        startSerialNumber: '',
        description: ''
      }}
      onSubmit={(values) => {
        createPartNumber({
          partNumber: values.partNumber,
          series: values.series,
          product: values.product,
          model: values.model,
          sku: values.sku,
          startSerialNumber: values.startSerialNumber,
          description: values.description
        });
      }}
    >
      {({
        handleChange,
        handleSubmit,
        values,
        isValid,
        setFieldTouched,
        errors,
        touched
      }) => (
        <>
          <StyledContainerItems>
            <StyledTitleSideBar>
              {Strings.manufacture.partNumber.title}
            </StyledTitleSideBar>
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.partNumber,
                type: 'inputText',
                currentValue: values.partNumber,
                nameAndId: 'partNumber',
                onChange: handleChange('partNumber'),
                onBlur: () => setFieldTouched('partNumber')
              }}
            />
            {touched.partNumber && errors.partNumber && (
              <ErrorText>{String(errors.partNumber)}</ErrorText>
            )}
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.product,
                type: 'inputText',
                currentValue: values.product,
                nameAndId: 'product',
                onChange: handleChange('product'),
                onBlur: () => setFieldTouched('product')
              }}
            />
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.series,
                type: 'inputText',
                currentValue: values.series,
                nameAndId: 'series',
                onChange: handleChange('series'),
                onBlur: () => setFieldTouched('series')
              }}
            />
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.model,
                type: 'inputText',
                currentValue: values.model,
                nameAndId: 'model',
                onChange: handleChange('model'),
                onBlur: () => setFieldTouched('model')
              }}
            />
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.sku,
                type: 'inputText',
                currentValue: values.sku,
                nameAndId: 'sku',
                onChange: handleChange('sku'),
                onBlur: () => setFieldTouched('sku')
              }}
            />
            {touched.sku && errors.sku && (
              <ErrorText>{String(errors.sku)}</ErrorText>
            )}
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.startSerialNumber,
                type: 'inputText',
                currentValue: values.startSerialNumber,
                nameAndId: 'startSerialNumber',
                onChange: handleChange('startSerialNumber'),
                onBlur: () => setFieldTouched('startSerialNumber'),
                placeholder:
                  'Use the following format PREFIX+NUMBERS(FODS00001)'
              }}
            />
            {touched.startSerialNumber && errors.startSerialNumber && (
              <ErrorText>{String(errors.startSerialNumber)}</ErrorText>
            )}
            <SideBarEditablePropertyItem
              item={{
                label: Strings.manufacture.partNumber.create.description,
                type: 'inputText',
                currentValue: values.description,
                nameAndId: 'description',
                onChange: handleChange('description'),
                onBlur: () => setFieldTouched('description')
              }}
            />
          </StyledContainerItems>
          <StyledContainerFooterSideBar>
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setOpenSideBar(false);
              }}
            >
              {Strings.common.cancel}
            </button>
            <Button
              variant="contained"
              size="large"
              className="btn-save"
              onClick={() => handleSubmit()}
            >
              {Strings.common.save}
            </Button>
          </StyledContainerFooterSideBar>
        </>
      )}
    </Formik>
  );
};
